import React from 'react';
import { Target } from 'lucide-react';
import type {
  CoachLayer,
  CoachLayerStatus,
  CoachPracticeItem,
  CoachPriority,
  CoachRecommendation,
  CoachWatchout,
} from '../../types/coachLayer';

export type CoachLayerSectionProps = {
  coachLayer?: CoachLayer | null;
};

const PRIORITY_RANK: Record<string, number> = {
  high_priority: 0,
  medium_priority: 1,
  low_priority: 2,
  watchout_only: 3,
  not_enough_evidence: 4,
};

export function formatCoachStatusLabel(status?: string): string {
  switch (status) {
    case 'ready':
      return 'Ready';
    case 'limited_sample':
      return 'Limited Sample';
    case 'unavailable':
      return 'Unavailable';
    case 'insufficient_analysis':
      return 'Insufficient Analysis';
    default:
      return 'Coach Guidance';
  }
}

export function formatCoachConfidenceLabel(confidence?: string): string {
  switch (confidence) {
    case 'high':
      return 'High';
    case 'medium':
      return 'Medium';
    case 'limited':
      return 'Limited';
    case 'insufficient':
      return 'Insufficient';
    default:
      return confidence ? String(confidence) : 'Limited';
  }
}

export function formatCoachPriorityLabel(priority?: string): string {
  switch (priority) {
    case 'high_priority':
      return 'High Priority';
    case 'medium_priority':
      return 'Medium Priority';
    case 'low_priority':
      return 'Low Priority';
    case 'watchout_only':
      return 'Watchout';
    case 'not_enough_evidence':
      return 'Not Enough Evidence';
    default:
      return '';
  }
}

export function hasRenderableCoachContent(coachLayer?: CoachLayer | null): boolean {
  if (!coachLayer) return false;

  const status = coachLayer.status;
  if (
    (status === 'unavailable' || status === 'insufficient_analysis') &&
    !(coachLayer.recommendations || []).length &&
    !(coachLayer.practice_plan || []).length &&
    !(coachLayer.watchouts || []).length &&
    !(coachLayer.limitations || []).length
  ) {
    return false;
  }

  const hasSummary = Boolean(String(coachLayer.summary || '').trim());
  const hasRecommendations = (coachLayer.recommendations || []).some(
    (item) => Boolean(String(item.title || '').trim()) || Boolean(String(item.action || '').trim())
  );
  const hasPractice = (coachLayer.practice_plan || []).some(
    (item) => Boolean(String(item.focus || '').trim())
  );
  const hasWatchouts = (coachLayer.watchouts || []).some(
    (item) => Boolean(String(item.title || '').trim()) || Boolean(String(item.what_to_watch || '').trim())
  );
  const hasLimitations = (coachLayer.limitations || []).some((item) =>
    Boolean(String(item || '').trim())
  );

  return hasSummary || hasRecommendations || hasPractice || hasWatchouts || hasLimitations;
}

function coachConfidenceBadgeClass(confidence?: string): string {
  if (confidence === 'high') {
    return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200';
  }
  if (confidence === 'medium') {
    return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
  }
  return 'bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300';
}

function coachStatusBadgeClass(status?: CoachLayerStatus | string): string {
  if (status === 'ready') {
    return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200';
  }
  if (status === 'limited_sample') {
    return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
  }
  return 'bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300';
}

function coachPriorityBadgeClass(priority?: CoachPriority | string): string {
  if (priority === 'high_priority') {
    return 'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-200';
  }
  if (priority === 'medium_priority') {
    return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
  }
  return 'bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300';
}

function pickRecommendationFields(item: CoachRecommendation): CoachRecommendation {
  return {
    type: item.type,
    category: item.category,
    priority: item.priority,
    title: item.title,
    rationale: item.rationale,
    action: item.action,
    confidence: item.confidence,
    sample_caveat: item.sample_caveat,
    evidence_status: item.evidence_status,
  };
}

function pickPracticeFields(item: CoachPracticeItem): CoachPracticeItem {
  return {
    focus: item.focus,
    why: item.why,
    drill_or_action: item.drill_or_action,
    confidence: item.confidence,
  };
}

function pickWatchoutFields(item: CoachWatchout): CoachWatchout {
  return {
    title: item.title,
    what_to_watch: item.what_to_watch,
    what_not_to_assume: item.what_not_to_assume,
    confidence: item.confidence,
  };
}

function rankRecommendations(recommendations: CoachRecommendation[]): CoachRecommendation[] {
  return [...recommendations]
    .map(pickRecommendationFields)
    .sort((a, b) => {
    const rankA = PRIORITY_RANK[String(a.priority || 'not_enough_evidence')] ?? 99;
    const rankB = PRIORITY_RANK[String(b.priority || 'not_enough_evidence')] ?? 99;
    return rankA - rankB;
  });
}

function RecommendationCard({ recommendation }: { recommendation: CoachRecommendation }) {
  const title = recommendation.title?.trim();
  const action = recommendation.action?.trim();
  const rationale = recommendation.rationale?.trim();
  const caveat = recommendation.sample_caveat?.trim();
  const priorityLabel = formatCoachPriorityLabel(recommendation.priority);
  const confidenceLabel = formatCoachConfidenceLabel(recommendation.confidence);

  if (!title && !action && !rationale) return null;

  return (
    <div className="bg-secondary-50 dark:bg-secondary-800/80 rounded-lg p-4 border border-secondary-200 dark:border-secondary-700">
      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
        {title && (
          <h4 className="font-semibold text-secondary-900 dark:text-white">{title}</h4>
        )}
        <div className="flex flex-wrap gap-2">
          {priorityLabel && (
            <span
              className={`px-2 py-0.5 rounded text-xs font-medium ${coachPriorityBadgeClass(
                recommendation.priority
              )}`}
            >
              {priorityLabel}
            </span>
          )}
          {confidenceLabel && (
            <span
              className={`px-2 py-0.5 rounded text-xs font-medium ${coachConfidenceBadgeClass(
                recommendation.confidence
              )}`}
            >
              {confidenceLabel}
            </span>
          )}
        </div>
      </div>
      {action && (
        <p className="text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">{action}</p>
      )}
      {rationale && (
        <p className="text-sm text-secondary-700 dark:text-secondary-300 mb-2 leading-relaxed">
          {rationale}
        </p>
      )}
      {caveat && (
        <p className="text-xs text-amber-800 dark:text-amber-200">{caveat}</p>
      )}
    </div>
  );
}

function PracticeCard({ item }: { item: CoachPracticeItem }) {
  const focus = item.focus?.trim();
  const drill = item.drill_or_action?.trim();
  const why = item.why?.trim();
  if (!focus && !drill) return null;

  return (
    <div className="bg-secondary-50 dark:bg-secondary-800/80 rounded-lg p-4 border border-secondary-200 dark:border-secondary-700">
      {focus && <h4 className="font-semibold text-secondary-900 dark:text-white mb-2">{focus}</h4>}
      {drill && (
        <p className="text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">{drill}</p>
      )}
      {why && <p className="text-sm text-secondary-700 dark:text-secondary-300">{why}</p>}
    </div>
  );
}

function WatchoutCard({ watchout }: { watchout: CoachWatchout }) {
  const title = watchout.title?.trim();
  const whatToWatch = watchout.what_to_watch?.trim();
  const whatNotToAssume = watchout.what_not_to_assume?.trim();
  if (!title && !whatToWatch) return null;

  return (
    <div className="bg-amber-50 dark:bg-amber-950/30 rounded-lg p-4 border border-amber-200 dark:border-amber-800/60">
      {title && <h4 className="font-semibold text-secondary-900 dark:text-white mb-2">{title}</h4>}
      {whatToWatch && (
        <p className="text-sm text-secondary-700 dark:text-secondary-300 mb-2">
          <span className="font-medium text-secondary-900 dark:text-white">Watch: </span>
          {whatToWatch}
        </p>
      )}
      {whatNotToAssume && (
        <p className="text-sm text-secondary-600 dark:text-secondary-400">
          <span className="font-medium text-secondary-900 dark:text-white">Do not assume: </span>
          {whatNotToAssume}
        </p>
      )}
    </div>
  );
}

const CoachLayerSection: React.FC<CoachLayerSectionProps> = ({ coachLayer }) => {
  if (!hasRenderableCoachContent(coachLayer)) {
    return null;
  }

  const layer = coachLayer as CoachLayer;
  const recommendations = rankRecommendations(layer.recommendations || []).slice(0, 3);
  const practicePlan = (layer.practice_plan || []).map(pickPracticeFields).slice(0, 3);
  const watchouts = (layer.watchouts || []).map(pickWatchoutFields).slice(0, 5);
  const limitations = (layer.limitations || []).filter((item) => String(item || '').trim());
  const summary = layer.summary?.trim();
  const statusLabel = formatCoachStatusLabel(layer.status);
  const confidenceLabel = formatCoachConfidenceLabel(layer.confidence);

  return (
    <div className="card border-2 border-primary-200 dark:border-primary-700">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Target className="h-5 w-5 text-primary-600 dark:text-primary-300" />
            <h2 className="text-2xl font-bold text-secondary-900 dark:text-white">Coach Layer</h2>
          </div>
          <p className="text-sm text-secondary-500 dark:text-secondary-400">
            Action guidance from the scouting report
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {layer.status && (
            <span
              className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${coachStatusBadgeClass(
                layer.status
              )}`}
            >
              {statusLabel}
            </span>
          )}
          {layer.confidence && (
            <span
              className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${coachConfidenceBadgeClass(
                layer.confidence
              )}`}
            >
              {confidenceLabel} confidence
            </span>
          )}
        </div>
      </div>

      {summary && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-secondary-900 dark:text-white mb-2">
            Coach Summary
          </h3>
          <p className="text-secondary-800 dark:text-secondary-200 leading-relaxed">{summary}</p>
        </div>
      )}

      {recommendations.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-secondary-900 dark:text-white mb-3">
            Top Adjustments
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            {recommendations.map((recommendation, index) => (
              <RecommendationCard
                key={`coach-rec-${recommendation.title || index}`}
                recommendation={recommendation}
              />
            ))}
          </div>
        </div>
      )}

      {practicePlan.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-secondary-900 dark:text-white mb-3">
            Practice This First
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            {practicePlan.map((item, index) => (
              <PracticeCard key={`coach-practice-${item.focus || index}`} item={item} />
            ))}
          </div>
        </div>
      )}

      {watchouts.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-secondary-900 dark:text-white mb-3">
            In-Game Watchouts
          </h3>
          <div className="grid gap-4">
            {watchouts.map((watchout, index) => (
              <WatchoutCard key={`coach-watchout-${watchout.title || index}`} watchout={watchout} />
            ))}
          </div>
        </div>
      )}

      {limitations.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-secondary-900 dark:text-white mb-2">
            Confidence Notes
          </h3>
          <ul className="list-disc list-inside space-y-1 text-sm text-secondary-600 dark:text-secondary-300">
            {limitations.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CoachLayerSection;
